import CheckIcon from '@mui/icons-material/Check'
import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material'
import useSize from '@react-hook/size'
import { useRef } from 'react'

type Props = {
  incomePotential: number
  outstandingCommission: number
  totalPaymentsReceived: number
}
export const ProjectProgressBar = (props: Props) => {
  const { incomePotential, outstandingCommission, totalPaymentsReceived } = props
  const target = useRef(null)
  const [containerWidth] = useSize(target)
  const containerSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    gap: 0,
    borderColor: (t) => t.palette.grey[300],
    borderRadius: (t) => `${t.shape.borderRadius}px`,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: 0,
    backgroundColor: (t) => t.palette.grey[100],
    position: 'relative',
    lineHeight: 1,
    height: 25,
    alignItems: 'stretch',
    overflow: 'hidden',
  }
  if (!incomePotential) {
    if (totalPaymentsReceived > 0) {
      return (
        <Box
          sx={{
            ...containerSx,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CheckIcon fontSize="small" sx={{ color: 'success' }} />
          <Typography variant="caption" color="success">
            Paid
          </Typography>
        </Box>
      )
    }
    return (
      <Box
        sx={{
          ...containerSx,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" px={2}>
          Pending
        </Typography>
      </Box>
    )
  }
  const paymentsWidthPerc = (totalPaymentsReceived / incomePotential) * 100
  const outstandingCommissionWidthPerc = (outstandingCommission / incomePotential) * 100
  const tooltipTableLegendDotBaseSx: SxProps<Theme> = {
    borderColor: (t) => t.palette.grey[300],
    borderRadius: (t) => `${t.shape.borderRadius}px`,
    borderWidth: 1,
    borderStyle: 'solid',
    padding: 0,
    backgroundColor: (t) => t.palette.grey[100],
    width: '10px',
    height: '10px',
  }
  return (
    <Tooltip
      placement="top"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
      title={
        <Box>
          <table>
            <tbody>
              <tr>
                <td>
                  <Box sx={tooltipTableLegendDotBaseSx} />
                </td>
                <td>Income Potential</td>
                <td align="right">{localCurrencyFormatter.format(incomePotential)}</td>
              </tr>
              <tr>
                <td>
                  <Box
                    sx={{
                      ...tooltipTableLegendDotBaseSx,
                      backgroundColor: (t) => t.palette.success.main,
                    }}
                  />
                </td>
                <td>Total Payments Received</td>
                <td align="right">{localCurrencyFormatter.format(totalPaymentsReceived)}</td>
              </tr>
              <tr>
                <td>
                  <Box
                    sx={{
                      ...tooltipTableLegendDotBaseSx,
                      backgroundColor: (t) => t.palette.warning.main,
                    }}
                  />
                </td>
                <td>Outstanding Commission</td>
                <td align="right">{localCurrencyFormatter.format(outstandingCommission)}</td>
              </tr>
            </tbody>
          </table>
        </Box>
      }
    >
      <Box sx={containerSx} ref={target}>
        <Box
          sx={{
            position: 'absolute',
            right: (t) => t.spacing(1),
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" lineHeight={1} sx={{ opacity: outstandingCommissionWidthPerc > 70 ? 0 : 1 }}>
            {localCurrencyFormatter.format(incomePotential)}
          </Typography>
        </Box>
        {totalPaymentsReceived > 0 && (
          <Box
            sx={{
              width: `${paymentsWidthPerc}%`,
              backgroundColor: (t) => t.palette.success.main,
              px: 1 / 2,
              color: (t) => t.palette.success.contrastText,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textShadow: '1px 1px 2px black',
                opacity: containerWidth * (paymentsWidthPerc / 100) < 100 ? 0 : 1,
              }}
            >
              {localCurrencyFormatter.format(totalPaymentsReceived)}
            </Typography>
          </Box>
        )}
        {outstandingCommission > 0 && (
          <Box
            sx={{
              width: `${(outstandingCommission / incomePotential) * 100}%`,
              backgroundColor: (t) => t.palette.warning.main,
              px: 1 / 2,
              color: (t) => t.palette.warning.contrastText,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textShadow: '1px 1px 2px black',
                opacity: containerWidth * (outstandingCommissionWidthPerc / 50) < 100 ? 0 : 1,
              }}
            >
              {localCurrencyFormatter.format(outstandingCommission)}
            </Typography>
          </Box>
        )}
      </Box>
    </Tooltip>
  )
}
export default ProjectProgressBar

export const localCurrencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  style: 'currency',
  maximumFractionDigits: 2,
  compactDisplay: 'short',
  trailingZeroDisplay: 'stripIfInteger',
  useGrouping: true,
})
